/**
 * UC-4 / UC-4B — Identity Review Queue
 * =====================================
 * BY CONVERSATION  — original individual conversation review (UC-4)
 * BY CORRESPONDENT — grouped review by external sender email (UC-4B)
 *
 * Dispositions (individual or group):
 *   link_client · link_lead · create_lead · create_inactive_client
 *   vendor · spam_marketing · internal_other · leave_unresolved
 *
 * All historical suppression flags remain immutable.
 * No operational side effects (no Penny, no SLA, no notifications).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

const CONTACT_TYPE_LABEL = {
  active_client: "Active Client",
  inactive_client: "Inactive Client",
  lead: "Lead",
  prospect: "Prospect",
};
const CONTACT_TYPE_TONE = {
  active_client: "bg-emerald-100 text-emerald-700",
  inactive_client: "bg-slate-100 text-slate-600",
  lead: "bg-blue-100 text-blue-700",
  prospect: "bg-indigo-100 text-indigo-700",
};

const MATCH_META = {
  exact_client_match:          { label: "Exact Client Match",         tone: "bg-emerald-100 text-emerald-700" },
  exact_inactive_client_match: { label: "Inactive Client Match",      tone: "bg-slate-100 text-slate-600" },
  exact_lead_match:            { label: "Exact Lead Match",           tone: "bg-blue-100 text-blue-700" },
  ambiguous:                   { label: "Ambiguous",                  tone: "bg-amber-100 text-amber-700" },
  no_match:                    { label: "No Match",                   tone: "bg-slate-100 text-slate-500" },
};

const ACTION_META = {
  link_client: {
    label: "Link to Client", icon: "UserCheck2",
    tone: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
    desc: "Associate all correspondence with an existing active or inactive client record.",
  },
  link_lead: {
    label: "Link to Lead", icon: "UserCog",
    tone: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",
    desc: "Associate all correspondence with an existing lead record.",
  },
  create_lead: {
    label: "Create New Lead", icon: "UserPlus2",
    tone: "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100",
    desc: "Create a new Lead from this correspondence. Pre-filled from correspondent data.",
  },
  create_inactive_client: {
    label: "Create Inactive Client", icon: "Archive",
    tone: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100",
    desc: "Former paying client not yet in Tax Flow. Creates a canonical inactive_client record. Historical relationship classification only — not proof of services.",
  },
  vendor: {
    label: "Vendor / Service", icon: "Building2",
    tone: "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100",
    desc: "Classify as a vendor, service provider, or third-party. No CRM record created.",
  },
  spam_marketing: {
    label: "Spam / Marketing", icon: "MailX",
    tone: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
    desc: "Classify as spam or unsolicited marketing. Excluded from normal views.",
  },
  internal_other: {
    label: "Internal / Other", icon: "Users",
    tone: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100",
    desc: "Internal firm correspondence, automated systems, or other non-client email.",
  },
  leave_unresolved: {
    label: "Leave Unresolved", icon: "Clock3",
    tone: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
    desc: "No change — leave in queue for later review. No database write.",
  },
};

const isClientType = (ct) => ["active_client", "inactive_client", "client"].includes(ct);
const isLeadType   = (ct) => ["lead", "prospect"].includes(ct);

// ── Main component ─────────────────────────────────────────────────────────────

export default function IdentityReviewQueue() {
  // ── view mode ──────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState("correspondent"); // "correspondent" | "conversation"
  const [queueSource, setQueueSource] = useState("historical"); // "historical" | "live_email"

  // ── stats ──────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);

  // ── BY CONVERSATION state ──────────────────────────────────────────────────
  const [items, setItems]     = useState(null);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId]   = useState(null);
  const [detail, setDetail]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── BY CORRESPONDENT state ─────────────────────────────────────────────────
  const [corrItems, setCorrItems]         = useState(null);
  const [corrTotal, setCorrTotal]         = useState(0);
  const [corrPage, setCorrPage]           = useState(1);
  const [corrLoading, setCorrLoading]     = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [corrDetail, setCorrDetail]       = useState(null);
  const [corrDetailLoading, setCorrDetailLoading] = useState(false);

  // ── shared action state ────────────────────────────────────────────────────
  const [activeAction, setActiveAction]   = useState(null);
  const [resolving, setResolving]         = useState(false);
  const [inlineConfirm, setInlineConfirm] = useState(null);
  const [linkModal, setLinkModal]         = useState(null);
  const [createLeadForm, setCreateLeadForm] = useState(null); // also used for create_inactive_client
  const [duplicateGuard, setDuplicateGuard] = useState(null); // 409 guard

  const searchTimerRef = useRef(null);

  // ── data loaders ───────────────────────────────────────────────────────────

  const loadStats = useCallback(() => {
    api.get("/communications/identity-review/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const loadList = useCallback((pg = 1) => {
    setLoading(true);
    const qs = queueSource === "live_email" ? "&queue_source=live_email" : "";
    api.get(`/communications/identity-review/queue?page=${pg}&per_page=25${qs}`)
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setPage(pg);
        if (pg === 1 && r.data.items?.length) {
          setSelectedId((cur) => cur || r.data.items[0].conv_id);
        }
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [queueSource]);

  const loadDetail = useCallback((convId) => {
    if (!convId) { setDetail(null); return; }
    setDetailLoading(true);
    api.get(`/communications/identity-review/queue/${convId}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, []);

  const loadCorrespondents = useCallback((pg = 1) => {
    setCorrLoading(true);
    api.get(`/communications/identity-review/correspondents?page=${pg}&per_page=50`)
      .then((r) => {
        setCorrItems(r.data.items || []);
        setCorrTotal(r.data.total || 0);
        setCorrPage(pg);
        if (pg === 1 && r.data.items?.length) {
          setSelectedEmail((cur) => cur || r.data.items[0]?.email || null);
        }
      })
      .catch(() => setCorrItems([]))
      .finally(() => setCorrLoading(false));
  }, []);

  const loadCorrespondentDetail = useCallback((email) => {
    if (!email) { setCorrDetail(null); return; }
    setCorrDetailLoading(true);
    cancelAction();
    api.get(`/communications/identity-review/correspondents/detail?email=${encodeURIComponent(email)}`)
      .then((r) => setCorrDetail(r.data))
      .catch(() => setCorrDetail(null))
      .finally(() => setCorrDetailLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── effects ────────────────────────────────────────────────────────────────

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (viewMode === "conversation") loadList(1);
  }, [viewMode, queueSource, loadList]);

  useEffect(() => {
    if (viewMode === "correspondent") loadCorrespondents(1);
  }, [viewMode, loadCorrespondents]);

  useEffect(() => {
    if (viewMode === "conversation") loadDetail(selectedId);
  }, [viewMode, selectedId, loadDetail]);

  useEffect(() => {
    if (viewMode === "correspondent") loadCorrespondentDetail(selectedEmail);
  }, [viewMode, selectedEmail, loadCorrespondentDetail]);

  // ── advanceToNext (conversation mode) ─────────────────────────────────────

  const advanceToNext = useCallback((resolvedConvId) => {
    setItems((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((i) => i.conv_id === resolvedConvId);
      const remaining = prev.filter((i) => i.conv_id !== resolvedConvId);
      const nextId = remaining[idx]?.conv_id || remaining[idx - 1]?.conv_id || null;
      setSelectedId(nextId);
      return remaining;
    });
    setTotal((t) => Math.max(0, t - 1));
    loadStats();
  }, [loadStats]);

  // ── advanceToNextCorrespondent ─────────────────────────────────────────────

  const advanceToNextCorrespondent = useCallback((resolvedEmail) => {
    setCorrItems((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((i) => i.email === resolvedEmail);
      const remaining = prev.filter((i) => i.email !== resolvedEmail);
      const next = remaining[idx]?.email || remaining[idx - 1]?.email || null;
      setSelectedEmail(next);
      return remaining;
    });
    setCorrTotal((t) => Math.max(0, t - 1));
    loadStats();
  }, [loadStats]);

  // ── action helpers ─────────────────────────────────────────────────────────

  const cancelAction = useCallback(() => {
    setActiveAction(null);
    setInlineConfirm(null);
    setLinkModal(null);
    setCreateLeadForm(null);
    setDuplicateGuard(null);
  }, []);

  // Current prefill (mode-aware)
  const getPrefill = useCallback(() => {
    if (viewMode === "correspondent") {
      return corrDetail?.create_prefill || {};
    }
    return detail?.create_lead_prefill || {};
  }, [viewMode, corrDetail, detail]);

  const startLinkAction = useCallback((mode) => {
    setActiveAction(mode);
    setInlineConfirm(null);
    setCreateLeadForm(null);
    setDuplicateGuard(null);
    const candidates = viewMode === "correspondent"
      ? (corrDetail?.match?.candidates || [])
      : (detail?.candidate_matches || []);
    setLinkModal({ mode, selectedCandidate: null, searchQ: "", searchResults: [], searching: false, candidates });
  }, [viewMode, corrDetail, detail]);

  const startCreateAction = useCallback((action) => {
    setActiveAction(action);
    setInlineConfirm(null);
    setLinkModal(null);
    setDuplicateGuard(null);
    const prefill = getPrefill();
    setCreateLeadForm({
      action,
      lead_name: prefill.lead_name || "",
      lead_email: prefill.lead_email || "",
      note: "",
    });
  }, [getPrefill]);

  const startInlineConfirm = useCallback((action) => {
    setActiveAction(action);
    setLinkModal(null);
    setCreateLeadForm(null);
    setDuplicateGuard(null);
    setInlineConfirm({ action });
  }, []);

  // Unified resolve that handles both modes
  const doResolve = useCallback(async (body) => {
    if (viewMode === "correspondent" && selectedEmail) {
      await resolveCorrespondent(selectedEmail, body);
    } else if (viewMode === "conversation" && selectedId) {
      await resolveConversation(selectedId, body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedEmail, selectedId]);

  // ── resolve: individual conversation ──────────────────────────────────────

  const resolveConversation = useCallback(async (convId, body) => {
    setResolving(true);
    try {
      await api.post(`/communications/identity-review/${convId}/resolve`, body);
      const isNoOp = body.action === "leave_unresolved";
      if (!isNoOp) {
        advanceToNext(convId);
      }
      toast.success(isNoOp ? "Left unresolved." : "Correspondence resolved.");
    } catch (err) {
      const errData = err?.response?.data;
      if (err?.response?.status === 409 && errData?.existing) {
        setDuplicateGuard({ existing: errData.existing, message: errData.message });
        return;
      }
      const msg = errData?.detail || "Resolution failed.";
      toast.error(typeof msg === "string" ? msg : msg?.message || "Resolution failed.");
    } finally {
      setResolving(false);
      setActiveAction(null);
      setLinkModal(null);
      setCreateLeadForm(null);
      setInlineConfirm(null);
    }
  }, [advanceToNext]);

  // ── resolveCorrespondent: whole group ──────────────────────────────────────

  const resolveCorrespondent = useCallback(async (email, body) => {
    setResolving(true);
    try {
      const result = await api.post("/communications/identity-review/correspondents/bulk-resolve", {
        email,
        ...body,
      });
      const isNoOp = body.action === "leave_unresolved";
      if (!isNoOp) {
        const n = result.data.conversations_resolved;
        toast.success(`${n} conversation${n !== 1 ? "s" : ""} resolved for ${email}`);
        advanceToNextCorrespondent(email);
      } else {
        toast.success("Group left unresolved.");
      }
    } catch (err) {
      const errData = err?.response?.data;
      if (err?.response?.status === 409 && errData?.existing) {
        setDuplicateGuard({ existing: errData.existing, message: errData.message });
        return;
      }
      const msg = errData?.detail || "Resolution failed.";
      toast.error(typeof msg === "string" ? msg : msg?.message || "Resolution failed.");
    } finally {
      setResolving(false);
      setActiveAction(null);
      setLinkModal(null);
      setCreateLeadForm(null);
      setInlineConfirm(null);
    }
  }, [advanceToNextCorrespondent]);

  // ── confirmation handlers ──────────────────────────────────────────────────

  const handleConfirmLink = useCallback(() => {
    if (!linkModal?.selectedCandidate) return;
    doResolve({ action: linkModal.mode, client_id: linkModal.selectedCandidate.id });
  }, [linkModal, doResolve]);

  const handleConfirmCreate = useCallback(() => {
    if (!createLeadForm) return;
    doResolve({
      action: createLeadForm.action,
      lead_name: createLeadForm.lead_name.trim(),
      lead_email: createLeadForm.lead_email.trim(),
      note: createLeadForm.note || undefined,
    });
  }, [createLeadForm, doResolve]);

  const handleInlineConfirm = useCallback(() => {
    if (!inlineConfirm) return;
    doResolve({ action: inlineConfirm.action });
  }, [inlineConfirm, doResolve]);

  // Link from duplicate guard
  const handleLinkDuplicate = useCallback(() => {
    if (!duplicateGuard?.existing) return;
    const existing = duplicateGuard.existing;
    const isLead = isLeadType(existing.contact_type || existing.lifecycle_status);
    setDuplicateGuard(null);
    doResolve({
      action: isLead ? "link_lead" : "link_client",
      client_id: existing.id,
    });
  }, [duplicateGuard, doResolve]);

  // ── link search ────────────────────────────────────────────────────────────

  const handleLinkSearch = useCallback((q) => {
    setLinkModal((m) => ({ ...m, searchQ: q }));
    clearTimeout(searchTimerRef.current);
    if (!q.trim()) { setLinkModal((m) => ({ ...m, searchResults: [], searching: false })); return; }
    setLinkModal((m) => ({ ...m, searching: true }));
    searchTimerRef.current = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}&type=contact&limit=10`)
        .then((r) => {
          const results = (r.data.results || r.data.items || []).filter((x) => x.type === "contact" || x.email);
          setLinkModal((m) => ({ ...m, searchResults: results, searching: false }));
        })
        .catch(() => setLinkModal((m) => ({ ...m, searchResults: [], searching: false })));
    }, 350);
  }, []);

  // ── action button click dispatcher ────────────────────────────────────────

  const onActionClick = useCallback((action) => {
    if (action === "link_client" || action === "link_lead") startLinkAction(action);
    else if (action === "create_lead" || action === "create_inactive_client") startCreateAction(action);
    else startInlineConfirm(action);
  }, [startLinkAction, startCreateAction, startInlineConfirm]);

  // ── current right-panel detail (mode-aware) ───────────────────────────────

  const rightLoading   = viewMode === "correspondent" ? corrDetailLoading : detailLoading;
  const rightIsEmpty   = viewMode === "correspondent"
    ? !corrDetail && !corrDetailLoading && !selectedEmail
    : !detail && !detailLoading && !selectedId;
  const rightHasItem   = viewMode === "correspondent" ? !!corrDetail : !!detail;
  const rightConvCount = viewMode === "correspondent" ? (corrDetail?.conversation_count || 0) : 1;

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-slate-50">

      {/* ── LEFT PANEL ── */}
      <aside className="w-80 shrink-0 flex flex-col border-r border-slate-200 bg-white min-h-0">

        {/* Header + stats */}
        <div className="px-4 py-3 border-b border-slate-200 space-y-3 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">Identity Review</div>
              {stats && (
                <div className="text-xs text-slate-400 mt-0.5">
                  <span className="text-amber-600 font-medium">{(stats.live_email_pending || 0).toLocaleString()} live email</span>
                  {" · "}
                  <span>{(stats.identity_pending || 0).toLocaleString()} historical</span>
                </div>
              )}
            </div>
            {stats && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${queueSource === "live_email" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}
                data-testid="ir-pending-badge"
              >
                {queueSource === "live_email"
                  ? (stats.live_email_pending || 0).toLocaleString()
                  : (stats.identity_pending || 0).toLocaleString()
                }
              </span>
            )}
          </div>

          {/* Source toggle: Live Email / Historical */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="ir-source-toggle">
            <button
              data-testid="ir-source-live-email"
              onClick={() => { setQueueSource("live_email"); setViewMode("conversation"); cancelAction(); setSelectedId(null); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-all ${
                queueSource === "live_email"
                  ? "bg-amber-50 text-amber-700 shadow-sm border border-amber-200"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Live Email {stats?.live_email_pending ? `(${stats.live_email_pending})` : ""}
            </button>
            <button
              data-testid="ir-source-historical"
              onClick={() => { setQueueSource("historical"); cancelAction(); setSelectedId(null); setSelectedEmail(null); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-all ${
                queueSource === "historical"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Historical {stats?.identity_pending ? `(${stats.identity_pending})` : ""}
            </button>
          </div>

          {/* View mode toggle — only for historical source */}
          {queueSource === "historical" && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="ir-mode-toggle">
            <button
              data-testid="ir-mode-correspondent"
              onClick={() => { setViewMode("correspondent"); cancelAction(); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-all ${
                viewMode === "correspondent"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              By Correspondent
            </button>
            <button
              data-testid="ir-mode-conversation"
              onClick={() => { setViewMode("conversation"); cancelAction(); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-all ${
                viewMode === "conversation"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              By Conversation
            </button>
          </div>
          )}
        </div>

        {/* List body */}
        <div className="flex-1 overflow-y-auto tf-scroll min-h-0">
          {viewMode === "correspondent" ? (
            <>
              {corrLoading && (
                <div className="flex items-center justify-center py-12">
                  <Icons.Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              )}
              {!corrLoading && corrItems?.length === 0 && (
                <div className="text-center py-12 px-4">
                  <Icons.CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <div className="text-sm font-medium text-slate-700">All correspondent groups resolved</div>
                  <div className="text-xs text-slate-400 mt-1">Switch to By Conversation to review remaining items.</div>
                </div>
              )}
              {corrItems?.map((item) => (
                <CorrespondentListItem
                  key={item.email}
                  item={item}
                  active={selectedEmail === item.email}
                  onClick={() => { setSelectedEmail(item.email); cancelAction(); }}
                />
              ))}
              {corrTotal > (corrPage * 50) && (
                <button
                  onClick={() => loadCorrespondents(corrPage + 1)}
                  className="w-full text-xs text-blue-600 hover:text-blue-800 py-2 border-t border-slate-100"
                >
                  Load more ({corrTotal - corrPage * 50} remaining)
                </button>
              )}
            </>
          ) : (
            <>
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Icons.Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              )}
              {!loading && items?.length === 0 && (
                <div className="text-center py-12 px-4">
                  <Icons.CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <div className="text-sm font-medium text-slate-700">All conversations reviewed</div>
                </div>
              )}
              {items?.map((item) => (
                <QueueListItem
                  key={item.conv_id}
                  item={item}
                  active={selectedId === item.conv_id}
                  onClick={() => { setSelectedId(item.conv_id); cancelAction(); }}
                />
              ))}
              {total > (page * 25) && (
                <button
                  onClick={() => loadList(page + 1)}
                  className="w-full text-xs text-blue-600 hover:text-blue-800 py-2 border-t border-slate-100"
                >
                  Load more ({total - page * 25} remaining)
                </button>
              )}
            </>
          )}
        </div>
      </aside>

      {/* ── RIGHT PANEL ── */}
      <section className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {rightLoading && (
          <div className="flex items-center justify-center h-full">
            <Icons.Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        )}

        {!rightLoading && rightIsEmpty && (
          <div className="flex items-center justify-center h-full text-slate-400">
            <div className="text-center">
              <Icons.Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">Select a {viewMode === "correspondent" ? "correspondent" : "conversation"} from the list</div>
            </div>
          </div>
        )}

        {!rightLoading && rightHasItem && (
          <div className="flex flex-col h-full min-h-0">

            {/* ── Detail header ── */}
            {viewMode === "correspondent" && corrDetail && (
              <div className="px-5 py-4 border-b border-slate-200 bg-white shrink-0">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                    <Icons.Mail className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-semibold text-slate-900 truncate" data-testid="ir-corr-detail-email">
                      {corrDetail.email}
                    </div>
                    {corrDetail.display_names?.length > 0 && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {corrDetail.display_names.slice(0, 3).join(" · ")}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="text-xs text-slate-500" data-testid="ir-corr-conv-count">
                        {corrDetail.conversation_count} conversation{corrDetail.conversation_count !== 1 ? "s" : ""}
                        {" · "}
                        {corrDetail.message_count} message{corrDetail.message_count !== 1 ? "s" : ""}
                      </span>
                      {corrDetail.has_pending_attachments && (
                        <span className="text-xs flex items-center gap-1 text-amber-600">
                          <Icons.Paperclip className="h-3 w-3" />
                          {corrDetail.pending_attachment_count} pending attachment{corrDetail.pending_attachment_count !== 1 ? "s" : ""}
                        </span>
                      )}
                      {corrDetail.first_sent && (
                        <span className="text-xs text-slate-400">
                          {fmtDate(corrDetail.first_sent)} – {fmtDate(corrDetail.last_sent)}
                        </span>
                      )}
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Historical · Suppressed</span>
                    </div>
                  </div>
                  {corrDetail.match?.match_type && MATCH_META[corrDetail.match.match_type] && (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${MATCH_META[corrDetail.match.match_type].tone}`}>
                      {MATCH_META[corrDetail.match.match_type].label}
                    </span>
                  )}
                </div>
              </div>
            )}

            {viewMode === "conversation" && detail && (
              <div className="px-5 py-4 border-b border-slate-200 bg-white shrink-0">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm truncate" data-testid="ir-detail-subject">
                      {detail.subject || "(no subject)"}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-xs text-slate-500">{detail.message_count} message{detail.message_count !== 1 ? "s" : ""}</span>
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Historical · Suppressed</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Scrollable detail body ── */}
            <div className="flex-1 overflow-y-auto tf-scroll px-5 py-4 space-y-5 min-h-0">

              {/* Correspondent: match section + samples */}
              {viewMode === "correspondent" && corrDetail && (
                <>
                  {/* CRM match */}
                  <CorrespondentMatchSection
                    match={corrDetail.match}
                    onLinkClient={() => startLinkAction("link_client")}
                    onLinkLead={() => startLinkAction("link_lead")}
                  />

                  {/* Attachment notice */}
                  {corrDetail.has_pending_attachments && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <Icons.Paperclip className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-amber-700">
                        <span className="font-medium">{corrDetail.pending_attachment_count} pending attachment{corrDetail.pending_attachment_count !== 1 ? "s" : ""}</span>
                        {" — "} fetched from Titan read-only and ingested via Governed Documents after relationship is established.
                      </div>
                    </div>
                  )}

                  {/* Sample conversations */}
                  {corrDetail.sample_conversations?.length > 0 && (
                    <div data-testid="ir-corr-sample-convs">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                        Sample Conversations ({corrDetail.sample_conversations.length} of {corrDetail.conversation_count} shown)
                      </div>
                      <div className="space-y-1.5">
                        {corrDetail.sample_conversations.map((conv) => (
                          <div key={conv.conv_id} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs" data-testid={`ir-corr-sample-${conv.conv_id}`}>
                            <div className="text-slate-700 font-medium truncate">{conv.subject}</div>
                            <div className="text-slate-400 mt-0.5 flex items-center gap-2">
                              <span>{conv.message_count} msg{conv.message_count !== 1 ? "s" : ""}</span>
                              <span>{fmtDate(conv.last_message_at)}</span>
                              {conv.has_pending_attachments && (
                                <span className="text-amber-600 flex items-center gap-0.5">
                                  <Icons.Paperclip className="h-3 w-3" />att
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Conversation: existing detail sections */}
              {viewMode === "conversation" && detail && (
                <>
                  <ParticipantsSection messages={detail.messages} />
                  <SuggestedMatches
                    candidates={detail.candidate_matches}
                    mode="conv"
                    onSelectLink={startLinkAction}
                  />
                  <ThreadPreview messages={detail.messages} />
                </>
              )}
            </div>

            {/* ── Action panel ── */}
            <div className="border-t border-slate-200 bg-white shrink-0 px-5 py-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                {viewMode === "correspondent"
                  ? `Resolve All ${rightConvCount} Conversation${rightConvCount !== 1 ? "s" : ""}`
                  : "Resolve Correspondence"}
              </div>

              {/* Duplicate guard */}
              {duplicateGuard && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="ir-duplicate-guard">
                  <Icons.AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-amber-800">Duplicate contact detected</div>
                    <div className="text-xs text-amber-700 mt-0.5">{duplicateGuard.message}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5 text-xs">
                        <span className="font-medium text-slate-800">{duplicateGuard.existing.full_name}</span>
                        <span className="text-slate-400 ml-1.5">{duplicateGuard.existing.email}</span>
                        <span className={`ml-1.5 text-[10px] px-1 py-0.5 rounded ${CONTACT_TYPE_TONE[duplicateGuard.existing.contact_type || duplicateGuard.existing.lifecycle_status] || "bg-slate-100 text-slate-600"}`}>
                          {CONTACT_TYPE_LABEL[duplicateGuard.existing.contact_type || duplicateGuard.existing.lifecycle_status] || "Contact"}
                        </span>
                      </div>
                      <button
                        data-testid="ir-dup-link-btn"
                        onClick={handleLinkDuplicate}
                        disabled={resolving}
                        className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded font-medium hover:bg-amber-700 disabled:opacity-50 shrink-0"
                      >
                        Link Existing
                      </button>
                      <button
                        data-testid="ir-dup-dismiss"
                        onClick={() => setDuplicateGuard(null)}
                        className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 shrink-0"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons grid */}
              {!duplicateGuard && (
                <div className="grid grid-cols-2 gap-1.5" data-testid="ir-action-buttons">
                  {Object.entries(ACTION_META).map(([action, meta]) => {
                    const IconCmp = Icons[meta.icon] || Icons.Zap;
                    const isActive = activeAction === action;
                    return (
                      <button
                        key={action}
                        data-testid={`ir-action-${action}`}
                        onClick={() => onActionClick(action)}
                        disabled={resolving}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all disabled:opacity-40 ${
                          isActive ? "ring-2 ring-offset-1 ring-blue-400 " : ""
                        }${meta.tone}`}
                      >
                        <IconCmp className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{meta.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Inline confirmation for non-relationship actions */}
              {inlineConfirm && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="ir-inline-confirm">
                  <Icons.AlertCircle className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="text-xs text-slate-700 flex-1">
                    {inlineConfirm.action === "leave_unresolved"
                      ? "Leave in queue unresolved? No changes will be made."
                      : viewMode === "correspondent"
                        ? `Classify all ${rightConvCount} conversations as "${ACTION_META[inlineConfirm.action]?.label}"?`
                        : `Classify as "${ACTION_META[inlineConfirm.action]?.label}"? This resolves the correspondence.`}
                  </span>
                  <button
                    data-testid="ir-inline-cancel"
                    onClick={cancelAction}
                    className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded border border-slate-200 hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="ir-inline-confirm-btn"
                    onClick={handleInlineConfirm}
                    disabled={resolving}
                    className="text-xs bg-slate-800 text-white px-3 py-1 rounded hover:bg-slate-700 disabled:opacity-50 font-medium"
                  >
                    {resolving ? "Saving…" : "Confirm"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── LINK MODAL ── */}
      {linkModal && (
        <Dialog open onOpenChange={cancelAction}>
          <DialogContent className="max-w-lg" data-testid="ir-link-modal">
            <DialogHeader>
              <DialogTitle>
                {linkModal.mode === "link_client" ? "Link to Existing Client" : "Link to Existing Lead"}
                {viewMode === "correspondent" && corrDetail && (
                  <span className="text-sm font-normal text-slate-500 ml-2">— {corrDetail.conversation_count} conversations</span>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {(linkModal.candidates?.length > 0 || detail?.candidate_matches?.length > 0) && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Suggested Matches</div>
                  <div className="space-y-1.5" data-testid="ir-link-candidates">
                    {(linkModal.candidates?.length > 0 ? linkModal.candidates : detail?.candidate_matches || [])
                      .filter((c) => {
                        const ct = c.contact_type || c.lifecycle_status;
                        if (linkModal.mode === "link_client") return isClientType(ct);
                        if (linkModal.mode === "link_lead") return isLeadType(ct);
                        return true;
                      })
                      .map((c) => (
                        <CandidateCard
                          key={c.id}
                          c={c}
                          selected={linkModal.selectedCandidate?.id === c.id}
                          onClick={() => setLinkModal((m) => ({ ...m, selectedCandidate: c, searchQ: "", searchResults: [] }))}
                          testIdSuffix={c.id}
                        />
                      ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  {(linkModal.candidates?.length > 0 || detail?.candidate_matches?.length > 0)
                    ? "Can't find the right person?"
                    : "Search Existing Records"}
                </div>
                <div className="relative">
                  <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    data-testid="ir-link-search-input"
                    value={linkModal.searchQ}
                    onChange={(e) => handleLinkSearch(e.target.value)}
                    placeholder={`Search existing ${linkModal.mode === "link_client" ? "Clients" : "Leads"}…`}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    autoFocus={!linkModal.candidates?.length && !detail?.candidate_matches?.length}
                  />
                  {linkModal.searching && (
                    <Icons.Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                  )}
                </div>
                {linkModal.searchResults.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid="ir-link-search-results">
                    {linkModal.searchResults
                      .filter((c) => {
                        const ct = c.contact_type || c.lifecycle_status;
                        if (linkModal.mode === "link_client") return !ct || isClientType(ct);
                        if (linkModal.mode === "link_lead") return !ct || isLeadType(ct);
                        return true;
                      })
                      .map((c) => (
                        <CandidateCard
                          key={c.id}
                          c={c}
                          selected={linkModal.selectedCandidate?.id === c.id}
                          onClick={() => setLinkModal((m) => ({ ...m, selectedCandidate: c }))}
                          testIdSuffix={`search-${c.id}`}
                        />
                      ))}
                  </div>
                )}
              </div>

              {linkModal.selectedCandidate && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="ir-link-selected">
                  <div className="text-xs font-medium text-blue-600 mb-1">Selected for linking:</div>
                  <div className="text-sm font-semibold text-slate-900">{linkModal.selectedCandidate.title || linkModal.selectedCandidate.full_name}</div>
                  <div className="text-xs text-slate-500">{linkModal.selectedCandidate.subtitle || linkModal.selectedCandidate.email}</div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <button data-testid="ir-link-cancel" onClick={cancelAction}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                data-testid="ir-link-confirm-btn"
                onClick={handleConfirmLink}
                disabled={!linkModal.selectedCandidate || resolving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 font-medium">
                {resolving ? "Linking…" : `Confirm ${linkModal.mode === "link_client" ? "Client" : "Lead"} Link`}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── CREATE LEAD / CREATE INACTIVE CLIENT MODAL ── */}
      {createLeadForm && (
        <Dialog open onOpenChange={cancelAction}>
          <DialogContent className="max-w-md" data-testid="ir-create-lead-modal">
            <DialogHeader>
              <DialogTitle>
                {createLeadForm.action === "create_inactive_client"
                  ? "Create Inactive Client Record"
                  : "Create New Lead from Correspondent"}
                {viewMode === "correspondent" && corrDetail && (
                  <span className="text-sm font-normal text-slate-500 ml-2">— {corrDetail.conversation_count} conversations</span>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className={`text-xs px-3 py-2 rounded-lg border ${
                createLeadForm.action === "create_inactive_client"
                  ? "text-orange-700 bg-orange-50 border-orange-200"
                  : "text-amber-700 bg-amber-50 border-amber-200"
              }`}>
                {createLeadForm.action === "create_inactive_client"
                  ? "Staff has identified this person as a former paying client not currently in Tax Flow. Creating this record is a relationship classification only — it does not reconstruct historical services, invoices, or payments."
                  : "Pre-filled from correspondent data. Review and correct before confirming. Creating a Lead is permanent."}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="ir-lead-name">Full Name</label>
                <input
                  id="ir-lead-name"
                  data-testid="ir-create-lead-name"
                  value={createLeadForm.lead_name}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, lead_name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="ir-lead-email">Email Address</label>
                <input
                  id="ir-lead-email"
                  data-testid="ir-create-lead-email"
                  value={createLeadForm.lead_email}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, lead_email: e.target.value }))}
                  placeholder="email@example.com"
                  type="email"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="ir-lead-note">Note (optional)</label>
                <textarea
                  id="ir-lead-note"
                  data-testid="ir-create-lead-note"
                  value={createLeadForm.note}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder={createLeadForm.action === "create_inactive_client"
                    ? "Context for this inactive client classification…"
                    : "Context for this lead creation…"}
                  rows={2}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <button
                data-testid="ir-create-lead-cancel"
                onClick={cancelAction}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                data-testid="ir-create-lead-confirm-btn"
                onClick={handleConfirmCreate}
                disabled={(!createLeadForm.lead_name.trim() && !createLeadForm.lead_email.trim()) || resolving}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-40 font-medium ${
                  createLeadForm.action === "create_inactive_client"
                    ? "bg-orange-600 hover:bg-orange-700"
                    : "bg-violet-600 hover:bg-violet-700"
                }`}
              >
                {resolving
                  ? "Creating…"
                  : createLeadForm.action === "create_inactive_client"
                    ? "Confirm Create Inactive Client"
                    : "Confirm Create Lead"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── sub-components ─────────────────────────────────────────────────────────────

function CorrespondentListItem({ item, active, onClick }) {
  const matchMeta = MATCH_META[item.match?.match_type] || MATCH_META.no_match;
  return (
    <button
      data-testid={`ir-corr-item-${(item.email || "").replace(/[@.]/g, "-")}`}
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${
        active ? "bg-blue-50 border-l-2 border-l-blue-500" : "hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-800 truncate">
            {item.display_names?.[0] || item.email}
          </div>
          <div className="text-[10px] text-slate-400 truncate font-mono">{item.email}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-slate-500">
              {item.conversation_count} conv{item.conversation_count !== 1 ? "s" : ""}
            </span>
            {item.has_pending_attachments && (
              <Icons.Paperclip className="h-3 w-3 text-amber-500" />
            )}
          </div>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${matchMeta.tone}`}>
          {matchMeta.label}
        </span>
      </div>
    </button>
  );
}

function CorrespondentMatchSection({ match, onLinkClient, onLinkLead }) {
  if (!match) return null;
  const { match_type, candidates } = match;

  if (match_type === "no_match") {
    return (
      <div data-testid="ir-corr-no-match" className="flex items-center gap-2 text-xs text-slate-400 italic">
        <Icons.Search className="h-3.5 w-3.5" />
        No existing contact found for this email address.
      </div>
    );
  }

  return (
    <div data-testid="ir-corr-match">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
        <Icons.UserSearch className="h-3.5 w-3.5 text-blue-400" />
        {match_type === "ambiguous" ? "Ambiguous — Multiple Matches" : "Deterministic Match"}
      </div>
      <div className="space-y-1.5">
        {candidates.map((c) => {
          const ct = c.contact_type || c.lifecycle_status;
          const isLead = isLeadType(ct);
          return (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white" data-testid={`ir-corr-candidate-${c.id}`}>
              <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold shrink-0">
                {(c.full_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{c.full_name}</div>
                <div className="text-[10px] text-slate-400 truncate">{c.email}</div>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${CONTACT_TYPE_TONE[ct] || "bg-slate-100 text-slate-600"}`}>
                {CONTACT_TYPE_LABEL[ct] || "Contact"}
              </span>
              {match_type !== "ambiguous" && (
                <button
                  data-testid={`ir-corr-quick-link-${c.id}`}
                  onClick={() => isLead ? onLinkLead() : onLinkClient()}
                  className="text-[10px] font-medium text-blue-600 hover:text-blue-800 shrink-0 hover:underline"
                >
                  Link All
                </button>
              )}
            </div>
          );
        })}
      </div>
      {match_type === "ambiguous" && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
          Multiple contacts match this email. Use Link to Client or Link to Lead to select the correct one, or review conversations individually.
        </div>
      )}
    </div>
  );
}

function QueueListItem({ item, active, onClick }) {
  return (
    <button
      data-testid={`ir-queue-item-${item.conv_id}`}
      onClick={onClick}
      className={`w-full flex flex-col gap-1 px-4 py-3 border-b border-slate-100 text-left transition-colors ${
        active ? "bg-blue-50 border-l-2 border-l-blue-500" : "hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-700 truncate">
            {item.correspondents?.[0] || "Unknown correspondent"}
            {item.correspondents?.length > 1 && (
              <span className="text-slate-400 ml-1">+{item.correspondents.length - 1}</span>
            )}
          </div>
          <div className="text-xs text-slate-500 truncate mt-0.5">{item.subject || "(no subject)"}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {item.has_pending_attachments && (
            <Icons.Paperclip className="h-3 w-3 text-amber-500" />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {item.date_range?.earliest && (
          <span className="text-[10px] text-slate-400">{fmtDate(item.date_range.earliest)}</span>
        )}
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">
          {item.message_count} msg{item.message_count !== 1 ? "s" : ""}
        </span>
        {item.candidate_count > 0 && (
          <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium" data-testid={`ir-queue-candidates-${item.conv_id}`}>
            {item.candidate_count} match{item.candidate_count !== 1 ? "es" : ""}
          </span>
        )}
      </div>
    </button>
  );
}

function CandidateCard({ c, selected, onClick, testIdSuffix }) {
  const ct = c.contact_type || c.lifecycle_status;
  return (
    <button
      data-testid={`ir-candidate-${testIdSuffix}`}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
        selected
          ? "border-blue-400 bg-blue-50 ring-1 ring-blue-300"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold shrink-0">
        {(c.full_name || c.title || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-800 truncate">{c.full_name || c.title}</div>
        <div className="text-[10px] text-slate-400 truncate">{c.email || c.subtitle}</div>
      </div>
      {ct && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${CONTACT_TYPE_TONE[ct] || "bg-slate-100 text-slate-600"}`}>
          {CONTACT_TYPE_LABEL[ct] || ct}
        </span>
      )}
    </button>
  );
}

function ParticipantsSection({ messages }) {
  if (!messages?.length) return null;
  const senders = [...new Set(messages.map((m) => m.sender_email).filter(Boolean))];
  if (!senders.length) return null;
  return (
    <div data-testid="ir-participants">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Correspondents</div>
      <div className="flex flex-wrap gap-1.5">
        {senders.map((s) => (
          <span key={s} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-mono">{s}</span>
        ))}
      </div>
    </div>
  );
}

function ThreadPreview({ messages }) {
  if (!messages?.length) return null;
  return (
    <div data-testid="ir-thread-preview">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
        Thread Preview ({messages.length} shown of up to 5)
      </div>
      <div className="space-y-2">
        {messages.map((msg, i) => (
          <div key={msg.id || i} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`ir-msg-${i}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                msg.direction === "inbound" ? "bg-blue-100 text-blue-600" :
                msg.direction === "outbound" ? "bg-emerald-100 text-emerald-600" :
                "bg-slate-100 text-slate-500"
              }`}>
                {msg.direction || "—"}
              </span>
              <span className="text-xs text-slate-500 font-medium truncate">
                {msg.sender_name || msg.sender_email || "Unknown"}
              </span>
              <span className="text-xs text-slate-400 ml-auto shrink-0">{fmtDate(msg.sent_at)}</span>
            </div>
            {msg.body_preview && (
              <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">{msg.body_preview}</p>
            )}
            {msg.pending_attachment_metadata?.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-600">
                <Icons.Paperclip className="h-3 w-3" />
                {msg.pending_attachment_metadata.length} pending attachment{msg.pending_attachment_metadata.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestedMatches({ candidates, mode, onSelectLink }) {
  if (!candidates?.length) return (
    <div className="text-xs text-slate-400 italic" data-testid="ir-no-candidates">
      No deterministic candidate matches found for this correspondent.
    </div>
  );
  return (
    <div data-testid="ir-suggested-matches">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
        <Icons.UserSearch className="h-3.5 w-3.5 text-blue-400" />
        Suggested Matches
        <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">{candidates.length}</span>
      </div>
      <div className="space-y-1.5">
        {candidates.map((c) => {
          const ct = c.contact_type || c.lifecycle_status;
          const isLead = isLeadType(ct);
          return (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white" data-testid={`ir-match-${c.id}`}>
              <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold shrink-0">
                {(c.full_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{c.full_name}</div>
                <div className="text-[10px] text-slate-400 truncate">{c.email}</div>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${CONTACT_TYPE_TONE[ct] || "bg-slate-100 text-slate-600"}`}>
                {CONTACT_TYPE_LABEL[ct] || "Contact"}
              </span>
              <button
                data-testid={`ir-match-link-${c.id}`}
                onClick={() => onSelectLink(isLead ? "link_lead" : "link_client")}
                className="text-[10px] font-medium text-blue-600 hover:text-blue-800 shrink-0 hover:underline"
              >
                Link
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
